import { getApiErrorCode, ACCOUNT_REQUIRED_CODE } from './listingAvailability';

/**
 * Buyer-facing copy for the delivery-city picker (deal v2, spec §7).
 *
 * GET /buyer/delivery/points is `@Protected({ forWallet, forAccount })`, so a wallet session whose
 * wallet is not linked to an account gets 403 ACCOUNT_REQUIRED — the common case, since a parallel
 * email session in the same browser used to satisfy the listing gate while the delivery calls
 * travel on the wallet token. The modal ignored the failure entirely and showed a dead, empty
 * "Select a city" list (found in the 12.08 live run); an empty-but-successful list looked the same.
 *
 * Kept pure for node-env unit tests (no RTL in this app).
 */
export const DELIVERY_POINTS_GENERIC_ERROR = 'Could not load the delivery cities. Please try again.';

export const DELIVERY_POINTS_ACCOUNT_REQUIRED_ERROR =
  'Sign up — or link this wallet to your account — before buying with delivery.';

export const DELIVERY_POINTS_EMPTY_NOTICE =
  'No delivery cities are available right now. Please try again later.';

export const describeDeliveryPointsError = (err: unknown): string =>
  getApiErrorCode(err) === ACCOUNT_REQUIRED_CODE
    ? DELIVERY_POINTS_ACCOUNT_REQUIRED_ERROR
    : DELIVERY_POINTS_GENERIC_ERROR;

export interface DeliveryPointsState {
  isLoading: boolean;
  /** The rejection from the query, if it failed (api error body or a plain string). */
  error?: unknown;
  /** How many cities came back. */
  count: number;
}

/**
 * The notice to show under the city select, or '' when the picker is usable (still loading, or
 * loaded with at least one city).
 */
export const resolveDeliveryPointsNotice = ({
  isLoading,
  error,
  count,
}: DeliveryPointsState): string => {
  if (isLoading) return '';
  if (error) return describeDeliveryPointsError(error);
  return count === 0 ? DELIVERY_POINTS_EMPTY_NOTICE : '';
};
